if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLElement {
      constructor() {
        super();
        this.tabs = [];
        this.panels = [];
        this.indicator = null;
        this.specGalleryStates = [];
      }

      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
        this.indicator = this.querySelector('.product-tabs__indicator');

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.selectTab(tab));
          tab.addEventListener('keydown', (e) => this.handleKeydown(e));
        });

        this.updateIndicator();
        this.specGalleryStates = [...this.querySelectorAll('[data-spec-gallery]')].map((gallery) =>
          this.initSpecGallery(gallery)
        );

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            if (!this.contains(e.target)) return;
            const index = this.panels.indexOf(e.target);
            if (index >= 0 && this.tabs[index]) {
              this.selectTab(this.tabs[index]);
            }
          });
        }
      }

      selectTab(selectedTab) {
        this.tabs.forEach((tab) => {
          const isSelected = tab === selectedTab;
          tab.setAttribute('aria-selected', isSelected);
          tab.setAttribute('tabindex', isSelected ? '0' : '-1');
        });

        const selectedId = selectedTab.getAttribute('aria-controls');
        this.panels.forEach((panel) => {
          panel.hidden = panel.id !== selectedId;
        });

        this.updateIndicator();
        requestAnimationFrame(() => this.specGalleryStates.forEach((state) => state.align()));
      }

      initSpecGallery(gallery) {
        const track = gallery.querySelector('[data-spec-track]');
        const slides = [...gallery.querySelectorAll('[data-spec-slide]')];
        const thumbnails = [...gallery.querySelectorAll('[data-spec-thumbnail]')];
        const previousButton = gallery.querySelector('[data-spec-previous]');
        const nextButton = gallery.querySelector('[data-spec-next]');
        const currentElement = gallery.querySelector('[data-spec-current]');
        const dialog = gallery.querySelector('[data-spec-dialog]');
        const dialogImage = gallery.querySelector('[data-spec-dialog-image]');
        const dialogPreviousButton = gallery.querySelector('[data-spec-dialog-previous]');
        const dialogNextButton = gallery.querySelector('[data-spec-dialog-next]');
        const dialogCurrentElement = gallery.querySelector('[data-spec-dialog-current]');
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let currentIndex = 0;
        let scrollTimer;
        let opener;

        const updateState = (index) => {
          currentIndex = Math.max(0, Math.min(index, slides.length - 1));

          slides.forEach((slide, slideIndex) => {
            const isActive = slideIndex === currentIndex;
            slide.classList.toggle('is-active', isActive);
            const link = slide.querySelector('[data-spec-image-open]');
            if (link) link.tabIndex = isActive ? 0 : -1;
          });

          thumbnails.forEach((thumbnail, thumbnailIndex) => {
            const isActive = thumbnailIndex === currentIndex;
            thumbnail.classList.toggle('is-active', isActive);
            if (isActive) thumbnail.setAttribute('aria-current', 'true');
            else thumbnail.removeAttribute('aria-current');
          });

          if (currentElement) currentElement.textContent = currentIndex + 1;
          if (previousButton) previousButton.disabled = currentIndex === 0;
          if (nextButton) nextButton.disabled = currentIndex === slides.length - 1;
        };

        const goTo = (index, shouldFocus = false) => {
          updateState(index);
          track.scrollTo({
            left: slides[currentIndex].offsetLeft,
            behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
          });
          if (shouldFocus) slides[currentIndex].querySelector('[data-spec-image-open]')?.focus();
        };

        const updateDialog = (index) => {
          updateState(index);
          const link = slides[currentIndex].querySelector('[data-spec-image-open]');
          const sourceImage = link?.querySelector('img');
          if (!link || !sourceImage || !dialogImage) return;

          dialogImage.src = link.href;
          dialogImage.srcset = sourceImage.srcset;
          dialogImage.sizes = '100vw';
          dialogImage.alt = sourceImage.alt;
          if (dialogCurrentElement) dialogCurrentElement.textContent = currentIndex + 1;
          if (dialogPreviousButton) dialogPreviousButton.disabled = currentIndex === 0;
          if (dialogNextButton) dialogNextButton.disabled = currentIndex === slides.length - 1;
        };

        previousButton?.addEventListener('click', () => goTo(currentIndex - 1));
        nextButton?.addEventListener('click', () => goTo(currentIndex + 1));
        thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener('click', () => goTo(index)));

        track?.addEventListener('scroll', () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const nearestIndex = slides.reduce((nearest, slide, index) => {
              const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
              return distance < nearest.distance ? { index, distance } : nearest;
            }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
            updateState(nearestIndex);
          }, 80);
        });

        gallery.addEventListener('keydown', (event) => {
          if (dialog?.open || !event.target.closest('[data-spec-image-open]')) return;
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goTo(currentIndex - 1, true);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goTo(currentIndex + 1, true);
          }
        });

        slides.forEach((slide, index) => {
          slide.querySelector('[data-spec-image-open]')?.addEventListener('click', (event) => {
            if (!dialog?.showModal) return;
            event.preventDefault();
            opener = event.currentTarget;
            updateDialog(index);
            dialog.showModal();
          });
        });

        dialogPreviousButton?.addEventListener('click', () => updateDialog(currentIndex - 1));
        dialogNextButton?.addEventListener('click', () => updateDialog(currentIndex + 1));
        dialog?.querySelector('[data-spec-dialog-close]')?.addEventListener('click', () => dialog.close());
        dialog?.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog?.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft' && currentIndex > 0) {
            event.preventDefault();
            updateDialog(currentIndex - 1);
          }
          if (event.key === 'ArrowRight' && currentIndex < slides.length - 1) {
            event.preventDefault();
            updateDialog(currentIndex + 1);
          }
        });
        dialog?.addEventListener('close', () => {
          dialogImage.removeAttribute('src');
          dialogImage.removeAttribute('srcset');
          opener?.focus();
        });

        updateState(0);

        return {
          align: () => {
            if (!track || !slides[currentIndex]) return;
            track.scrollLeft = slides[currentIndex].offsetLeft;
          },
        };
      }

      updateIndicator() {
        if (!this.indicator) return;
        const activeTab = this.querySelector('[role="tab"][aria-selected="true"]');
        if (!activeTab) return;

        const tablist = activeTab.parentElement;
        const left = activeTab.offsetLeft - tablist.scrollLeft;
        const width = activeTab.offsetWidth;

        this.style.setProperty('--tab-indicator-left', `${left}px`);
        this.style.setProperty('--tab-indicator-width', `${width}px`);
      }

      handleKeydown(e) {
        const currentIndex = this.tabs.indexOf(e.currentTarget);
        let newIndex;

        switch (e.key) {
          case 'ArrowRight':
            newIndex = (currentIndex + 1) % this.tabs.length;
            break;
          case 'ArrowLeft':
            newIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
            break;
          case 'Home':
            newIndex = 0;
            break;
          case 'End':
            newIndex = this.tabs.length - 1;
            break;
          default:
            return;
        }

        e.preventDefault();
        this.tabs[newIndex].focus();
        this.selectTab(this.tabs[newIndex]);
      }
    }
  );
}
